import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  getDisplayCaseProgram,
  getMyRebates,
  requestDisplayCase,
  type DisplayCaseInfo,
  type CustomerPurchase,
} from '../services/api';

export default function DisplayCaseProgram() {
  const [programActive, setProgramActive] = useState(false);
  const [rebatePercent, setRebatePercent] = useState(0);
  const [rebateDescription, setRebateDescription] = useState('');
  const [cases, setCases] = useState<DisplayCaseInfo[]>([]);
  const [purchases, setPurchases] = useState<CustomerPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [expandedPurchase, setExpandedPurchase] = useState<string | null>(null);

  // Detail modal state
  const [selectedCase, setSelectedCase] = useState<DisplayCaseInfo | null>(null);
  const [confirmingRequest, setConfirmingRequest] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [programData, rebateData] = await Promise.all([
        getDisplayCaseProgram(),
        getMyRebates(),
      ]);

      setProgramActive(programData.programActive);
      setRebatePercent(programData.rebatePercent);
      setRebateDescription(programData.rebateDescription);
      setCases(programData.cases);
      setPurchases(rebateData.purchases);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCaseDetail = (dc: DisplayCaseInfo) => {
    setSelectedCase(dc);
    setConfirmingRequest(false);
  };

  const closeCaseDetail = () => {
    if (isRequesting) return;
    setSelectedCase(null);
    setConfirmingRequest(false);
  };

  const handleConfirmRequest = async () => {
    if (!selectedCase) return;
    try {
      setIsRequesting(true);
      setSuccessMessage(null);
      setError(null);
      const result = await requestDisplayCase(selectedCase.caseId);
      setSuccessMessage(result.message);
      setSelectedCase(null);
      setConfirmingRequest(false);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request');
      setSelectedCase(null);
      setConfirmingRequest(false);
    } finally {
      setIsRequesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="w-12 h-12 border-4 border-workshop-elevated border-t-accent rounded-full mx-auto"
          />
          <p className="mt-4 text-text-muted uppercase tracking-wide text-sm">
            Loading display case program...
          </p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="space-y-8"
    >
      {/* Hero Section */}
      <div className="card bg-workshop text-cream text-center py-10 px-6 border border-white/[0.06]">
        <h1 className="text-3xl font-display tracking-extra-wide text-accent mb-3">
          Display Case Program
        </h1>
        <p className="text-text-muted max-w-2xl mx-auto text-lg">
          {rebateDescription || 'Showcase Top Dog Brand products in your store with our premium display cases and earn back the cost through rebates on every reorder.'}
        </p>
        {!programActive && (
          <div className="mt-4 inline-flex items-center gap-2 bg-white/5 px-4 py-2 rounded-sm">
            <span className="w-2 h-2 bg-amber-400 rounded-full" />
            <span className="text-sm text-text-muted">
              Program is currently unavailable. Check back soon!
            </span>
          </div>
        )}
      </div>

      {/* How It Works */}
      <div>
        <h2 className="text-xl font-display text-cream tracking-wide mb-4 text-center">
          How It Works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              step: '1',
              title: 'Purchase a Display Case',
              desc: 'Choose a premium display case designed to showcase Top Dog Brand products in your store.',
            },
            {
              step: '2',
              title: 'Place Reorders',
              desc: `Every time you reorder through the wholesale portal, ${rebatePercent}% of your order total is applied as a rebate.`,
            },
            {
              step: '3',
              title: 'Earn It Back',
              desc: 'Your rebates accumulate until the full cost of the display case has been earned back.',
            },
          ].map((item) => (
            <div key={item.step} className="card text-center">
              <div className="w-10 h-10 bg-accent text-workshop rounded-full flex items-center justify-center text-lg font-display mx-auto mb-3">
                {item.step}
              </div>
              <h3 className="font-display text-cream tracking-wide mb-2">{item.title}</h3>
              <p className="text-sm text-text-muted">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Success / Error Messages */}
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-green-900/30 border border-green-700/30 rounded-sm text-green-300 text-sm flex items-center justify-between"
        >
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="text-green-400 hover:text-green-200">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </motion.div>
      )}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-red-900/30 border border-red-700/30 rounded-sm text-red-300 text-sm flex items-center justify-between"
        >
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-200">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </motion.div>
      )}

      {/* Available Cases */}
      {programActive && cases.length > 0 && (
        <div>
          <h2 className="text-xl font-display text-cream tracking-wide mb-4">
            Available Display Cases
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cases.map((dc) => {
              const alreadyRequested = purchases.some(
                (p) => p.caseId === dc.caseId && p.status !== 'cancelled'
              );

              return (
                <motion.div
                  key={dc.caseId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card"
                >
                  {dc.imageUrl && (
                    <img
                      src={dc.imageUrl}
                      alt={dc.name}
                      className="w-full h-48 object-cover rounded-sm mb-4"
                    />
                  )}
                  <h3 className="font-display text-cream tracking-wide text-lg mb-1">
                    {dc.name}
                  </h3>
                  {dc.description && (
                    <p className="text-sm text-text-muted mb-3">{dc.description}</p>
                  )}
                  <div className="text-sm space-y-1 mb-4">
                    <p>
                      <span className="text-text-muted">Starting at:</span>{' '}
                      <span className="font-semibold text-accent text-lg">
                        ${dc.price.toFixed(2)}
                      </span>
                    </p>
                    {dc.dimensions && (
                      <p>
                        <span className="text-text-muted">Est. Dimensions:</span>{' '}
                        <span className="text-cream">{dc.dimensions}</span>
                      </p>
                    )}
                    <p className="text-xs text-accent font-medium">
                      {rebatePercent}% rebate on every reorder
                    </p>
                  </div>

                  {alreadyRequested ? (
                    <div className="px-4 py-2 text-center text-sm text-text-muted border border-white/10 rounded-sm bg-white/5">
                      Already requested
                    </div>
                  ) : (
                    <button
                      onClick={() => openCaseDetail(dc)}
                      className="w-full btn-primary"
                    >
                      View Details
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* My Cases / Rebate Progress */}
      {purchases.length > 0 && (
        <div>
          <h2 className="text-xl font-display text-cream tracking-wide mb-4">
            My Display Cases
          </h2>
          <div className="space-y-4">
            {purchases.map((p) => {
              const progressPct = p.purchasePrice > 0
                ? Math.min(100, (p.totalRebateEarned / p.purchasePrice) * 100)
                : 0;
              const sk = `${p.cognitoSub}#${p.caseId}`;
              const isExpanded = expandedPurchase === sk;

              const statusColors: Record<string, string> = {
                requested: 'bg-amber-900/40 text-amber-300',
                approved: 'bg-blue-900/40 text-blue-300',
                active: 'bg-green-900/40 text-green-300',
                completed: 'bg-purple-900/40 text-purple-300',
                cancelled: 'bg-white/5 text-text-muted',
              };

              return (
                <div key={sk} className="card">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-cream">{p.caseName}</h3>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-sm font-medium ${statusColors[p.status] || ''}`}
                        >
                          {p.status}
                        </span>
                      </div>
                      <p className="text-sm text-text-muted">
                        Purchased: ${p.purchasePrice.toFixed(2)} &middot;{' '}
                        Requested: {new Date(p.requestedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-text-muted">Rebate Earned</p>
                      <p className="text-lg font-semibold text-accent">
                        ${p.totalRebateEarned.toFixed(2)}
                        <span className="text-sm text-text-muted font-normal">
                          {' '}/ ${p.purchasePrice.toFixed(2)}
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-3">
                    <div className="bg-white/10 rounded-full h-3">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progressPct}%` }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                        className={`h-3 rounded-full ${
                          p.status === 'completed' ? 'bg-green-500' : 'bg-accent'
                        }`}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-text-muted mt-1">
                      <span>{progressPct.toFixed(0)}% earned back</span>
                      <span>${Math.max(0, p.remainingBalance).toFixed(2)} remaining</span>
                    </div>
                  </div>

                  {/* Expandable Rebate History */}
                  {p.rebates && p.rebates.length > 0 && (
                    <div className="mt-3">
                      <button
                        onClick={() => setExpandedPurchase(isExpanded ? null : sk)}
                        className="text-xs text-accent hover:text-accent/80 uppercase tracking-wide font-medium"
                      >
                        {isExpanded ? 'Hide' : 'Show'} Rebate History ({p.rebates.length})
                      </button>

                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-2 bg-white/5 rounded-sm p-3"
                        >
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-text-muted">
                                <th className="py-1 text-xs font-medium">Date</th>
                                <th className="py-1 text-xs font-medium">Order</th>
                                <th className="py-1 text-xs font-medium text-right">Total</th>
                                <th className="py-1 text-xs font-medium text-right">Rebate</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.rebates.map((r, idx) => (
                                <tr key={idx} className="border-t border-white/[0.06]">
                                  <td className="py-1.5 text-cream">
                                    {new Date(r.createdAt).toLocaleDateString()}
                                  </td>
                                  <td className="py-1.5 text-cream">{r.orderName}</td>
                                  <td className="py-1.5 text-right font-mono text-cream">
                                    ${r.orderTotal.toFixed(2)}
                                  </td>
                                  <td className="py-1.5 text-right font-mono text-green-400">
                                    +${r.cappedAmount.toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </motion.div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Detail Modal */}
      {selectedCase && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={closeCaseDetail}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative bg-workshop-surface rounded-sm shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-white/[0.06]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeCaseDetail}
              disabled={isRequesting}
              className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-workshop/80 hover:bg-workshop text-cream hover:text-accent transition-colors shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Large Image */}
            {selectedCase.imageUrl && (
              <img
                src={selectedCase.imageUrl}
                alt={selectedCase.name}
                className="w-full h-64 object-cover"
              />
            )}

            {/* Case Details */}
            <div className="p-6">
              <h2 className="text-2xl font-display text-cream tracking-wide mb-2">
                {selectedCase.name}
              </h2>
              {selectedCase.description && (
                <p className="text-text-muted mb-4">{selectedCase.description}</p>
              )}

              <div className="space-y-2 mb-4">
                <div className="flex items-baseline justify-between">
                  <span className="text-text-muted text-sm">Starting at</span>
                  <span className="text-2xl font-semibold text-accent">
                    ${selectedCase.price.toFixed(2)}
                  </span>
                </div>
                {selectedCase.dimensions && (
                  <div className="flex items-baseline justify-between">
                    <span className="text-text-muted text-sm">Est. Dimensions</span>
                    <span className="text-sm text-cream">{selectedCase.dimensions}</span>
                  </div>
                )}
                <div className="flex items-baseline justify-between">
                  <span className="text-text-muted text-sm">Rebate</span>
                  <span className="text-sm text-accent font-medium">
                    {rebatePercent}% back on every reorder
                  </span>
                </div>
              </div>

              <p className="text-xs text-text-muted mb-6">
                Each display case is custom-built. Final price and dimensions may vary based on your store's specific needs.
              </p>

              <div className="border-t border-white/[0.06] pt-4">
                {/* Confirmation Step */}
                {confirmingRequest ? (
                  <div>
                    <div className="bg-amber-900/20 border border-amber-700/30 rounded-sm p-4 mb-4">
                      <p className="text-sm text-amber-200">
                        You are requesting <strong>{selectedCase.name}</strong> (starting
                        at <strong>${selectedCase.price.toFixed(2)}</strong>). A team member
                        will review your request and confirm final pricing and specifications.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={handleConfirmRequest}
                        disabled={isRequesting}
                        className="flex-1 btn-accent"
                      >
                        {isRequesting ? 'Submitting...' : 'Confirm Request'}
                      </button>
                      <button
                        onClick={() => setConfirmingRequest(false)}
                        disabled={isRequesting}
                        className="px-4 py-2.5 border border-white/10 text-cream rounded-sm hover:bg-white/5 transition-colors uppercase tracking-wide text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmingRequest(true)}
                    className="w-full btn-accent"
                  >
                    Request This Case
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
}
